import {
  faFacebook,
  faGithub,
  faInstagram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import centeredImage from "../assets/profile.webp";
import styled from "styled-components";

const Container = styled.div`
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;

  z-index: 99;
`;

const ProfileImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 1;
`;

const ProfileImageFrame = styled.div`
  position: relative;
  width: 30vh;
  height: 30vh;
  border-radius: 50%;
  transform-style: preserve-3d;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: -18px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(71, 234, 255, 0.3),
      rgba(71, 234, 255, 0.08) 48%,
      transparent 72%
    );
    filter: blur(10px);
    opacity: 0;
    pointer-events: none;
    transform: perspective(900px) rotateX(var(--tilt-x))
      rotateY(var(--tilt-y));
    transform-origin: center;
    transition: opacity 0.3s, transform 140ms ease-out;
    will-change: transform;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -5px;
    border: 5px solid #ffffff;
    border-radius: 50%;
    pointer-events: none;
    transform-style: preserve-3d;
    transform: perspective(900px) rotateX(var(--tilt-x))
      rotateY(var(--tilt-y));
    transform-origin: center;
    transition: border-color 0.3s, box-shadow 0.3s,
      transform 140ms ease-out;
    will-change: transform;
    z-index: 2;
  }
`;

const SocialMediaContainer = styled.div`
  margin-top: 2vh;
  border: 5px solid rgba(255, 255, 255, 1);
  background: radial-gradient(
      circle at var(--pointer-x) var(--pointer-y),
      rgba(71, 234, 255, 0.14),
      transparent 45%
    ),
    rgba(0, 0, 0, 0.5);
  padding-left: 1vw;
  padding-right: 1vw;
  padding-top: 1vh;
  padding-bottom: 1vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  border-radius: 10px;
  color: white;
  transition: background-color 0.3s, border-color 0.3s, box-shadow 0.3s,
    color 0.3s;
`;

const ProfileGroup = styled.div`
  --tilt-x: 0deg;
  --tilt-y: 0deg;
  --pointer-x: 50%;
  --pointer-y: 50%;

  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover ${ProfileImageFrame}::after {
    border-color: #47eaff;
    box-shadow: 0 0 18px rgba(71, 234, 255, 0.55);
  }

  &:hover ${ProfileImageFrame}::before {
    opacity: 1;
  }

  &:hover ${SocialMediaContainer} {
    background-color: rgba(0, 0, 0, 0.7);
    border-color: #47eaff;
    box-shadow: 0 0 24px rgba(71, 234, 255, 0.28),
      inset 0 0 16px rgba(71, 234, 255, 0.08);
    color: #47eaff;
  }

  @media (prefers-reduced-motion: reduce) {
    ${ProfileImageFrame}::before,
    ${ProfileImageFrame}::after {
      transform: none;
      transition: border-color 0.3s, box-shadow 0.3s, opacity 0.3s;
    }
  }
`;

const SocialMediaIconRow = styled.div`
  display: flex;
  gap: 40px;
`;

const SocialMediaIcon = styled(FontAwesomeIcon)`
  font-size: 40px;
  transition: color 0.3s;
  cursor: pointer;
  color: #ffffff !important;

  &&:hover {
    color: #ffd166 !important;
  }
`;

const Header = styled.h1`
  font-size: 40px;
  margin-bottom: 0;
  margin-top: 0;
  color: #ffffff;
`;

const DownloadCVLink = styled.a`
  font-size: 20px;
  text-decoration: none;
  color: white;
  transition: color 0.3s;

  &&:hover,
  &&:focus-visible {
    color: #ffd166;
  }
`;

const cvUrl = "/CV/Elar%20Saks%20(CV).pdf";

const socialMediaLinks = {
  github: "https://github.com/elarsaks",
  linkedin: "https://www.linkedin.com/in/elarsaks/",
  facebook: "https://www.facebook.com/elar.saks/",
  instagram: "https://www.instagram.com/elarsaks/",
};

const ProfileSection: React.FC = () => {
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;

    const frame = event.currentTarget;
    const group = frame.parentElement;
    if (!group) return;

    const bounds = frame.getBoundingClientRect();
    const normalizedX =
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    const normalizedY =
      ((event.clientY - bounds.top) / bounds.height) * 2 - 1;
    const pointerX = ((normalizedX + 1) / 2) * 100;
    const pointerY = ((normalizedY + 1) / 2) * 100;

    group.style.setProperty("--tilt-x", `${-normalizedY * 7}deg`);
    group.style.setProperty("--tilt-y", `${normalizedX * 7}deg`);
    group.style.setProperty("--pointer-x", `${pointerX}%`);
    group.style.setProperty("--pointer-y", `${pointerY}%`);
  };

  const resetTilt = (event: React.PointerEvent<HTMLDivElement>) => {
    const group = event.currentTarget.parentElement;
    if (!group) return;

    group.style.setProperty("--tilt-x", "0deg");
    group.style.setProperty("--tilt-y", "0deg");
    group.style.setProperty("--pointer-x", "50%");
    group.style.setProperty("--pointer-y", "50%");
  };

  return (
    <Container>
      <ProfileGroup>
        <ProfileImageFrame
          onPointerMove={handlePointerMove}
          onPointerLeave={resetTilt}
        >
          <ProfileImage src={centeredImage} alt="Profile photo of Elar Saks" />
        </ProfileImageFrame>

        <SocialMediaContainer>
          <Header>Elar Saks</Header>
          <nav aria-label="Social media links">
            <SocialMediaIconRow>
              <a
                href={socialMediaLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Elar Saks' GitHub profile"
              >
                <SocialMediaIcon icon={faGithub} aria-hidden="true" />
              </a>
              <a
                href={socialMediaLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Elar Saks' LinkedIn profile"
              >
                <SocialMediaIcon icon={faLinkedin} aria-hidden="true" />
              </a>
              <a
                href={socialMediaLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Elar Saks' Facebook profile"
              >
                <SocialMediaIcon icon={faFacebook} aria-hidden="true" />
              </a>
              <a
                href={socialMediaLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Elar Saks' Instagram profile"
              >
                <SocialMediaIcon icon={faInstagram} aria-hidden="true" />
              </a>
            </SocialMediaIconRow>
          </nav>

          <DownloadCVLink
            href={cvUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Elar Saks' CV (opens in new window)"
          >
            Download CV
          </DownloadCVLink>
        </SocialMediaContainer>
      </ProfileGroup>
    </Container>
  );
};

export default ProfileSection;
